import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, LogIn, UserPlus, Building2, Briefcase } from "lucide-react";
import { login, register, dashboardPathFor } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";
import { IMG } from "@/lib/images";

/** Universal /login + /register page with role selector for register flow. */
export default function AuthPage({ mode = "login" }) {
  const nav = useNavigate();
  const loc = useLocation();
  const isRegister = mode === "register";
  const [role, setRole] = useState(new URLSearchParams(loc.search).get("role") || "advertiser");
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "", phone: "", mall_names: "" });
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        const data = await register({ ...form, role });
        if (data.access_token) {
          toast.success(`Welcome, ${data.user.name}.`);
          nav(dashboardPathFor(data.user));
        } else {
          toast.success("Account submitted. Our team will email you within 24 hours after review.");
          nav("/login");
        }
      } else {
        const user = await login(form.email, form.password);
        toast.success(`Welcome back, ${user.name}.`);
        nav(dashboardPathFor(user));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid={`auth-${mode}`} className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={IMG.hero} alt="" className="absolute inset-0 w-full h-full object-cover img-luxe" />
        <div className="absolute inset-0 hero-overlay-strong" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/85 hover:text-[var(--gold-light)]">
            <ArrowLeft size={12} /> Back to fundle
          </Link>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--gold-light)] font-semibold">
              <span className="inline-block w-8 h-px bg-[var(--gold-light)] align-middle mr-3" />
              Fundle · Space on Hiring
            </div>
            <h1 className="mt-5 font-serif-display text-5xl lg:text-6xl xl:text-7xl leading-[0.95]">
              India&apos;s mall ad spaces — <span className="font-serif-italic text-[var(--gold-light)]">in your dashboard.</span>
            </h1>
            <p className="mt-6 text-white/80 max-w-md">
              {isRegister
                ? "Open your Fundle account in two minutes. Advertisers go live instantly; mall admins are activated after a 24-hour review."
                : "Sign in to your advertiser, mall-admin or admin console."}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-[var(--bone)] flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[var(--gold)]">
            {isRegister ? "Create account" : "Sign in"}
          </div>
          <h2 className="mt-3 font-serif-display text-4xl lg:text-5xl text-[var(--ink)]">
            {isRegister ? <>Open your <span className="font-serif-italic text-[var(--gold)]">Fundle</span> account.</> : <>Welcome back.</>}
          </h2>

          {isRegister && (
            <div className="mt-7 grid grid-cols-2 gap-3">
              <RoleCard
                active={role === "advertiser"} onClick={() => setRole("advertiser")}
                icon={Briefcase} title="Advertiser / Agency"
                body="Browse, plan and book mall inventory."
                testId="role-advertiser"
              />
              <RoleCard
                active={role === "mall_owner"} onClick={() => setRole("mall_owner")}
                icon={Building2} title="Mall Admin"
                body="List inventory, manage availability."
                testId="role-mall_owner"
              />
            </div>
          )}

          <form className="mt-7 space-y-4" onSubmit={submit}>
            {isRegister && (
              <>
                <Field label="Full name">
                  <input data-testid="auth-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </Field>
                <Field label="Company">
                  <input data-testid="auth-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={role === "mall_owner" ? "e.g. Phoenix Mills" : "e.g. Acme India"} />
                </Field>
                <Field label="Phone">
                  <input data-testid="auth-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </Field>
                {role === "mall_owner" && (
                  <Field label="Malls you operate">
                    <input data-testid="auth-malls" value={form.mall_names} onChange={(e) => setForm({ ...form, mall_names: e.target.value })} placeholder="Comma-separated mall names" />
                  </Field>
                )}
              </>
            )}
            <Field label="Work email">
              <input data-testid="auth-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label={isRegister ? "Choose password (min 8 chars)" : "Password"}>
              <input data-testid="auth-password" type="password" required minLength={isRegister ? 8 : 1} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </Field>
            <button type="submit" data-testid="auth-submit" disabled={loading} className="btn-primary w-full justify-center">
              {isRegister ? <UserPlus size={14} /> : <LogIn size={14} />}
              {loading ? "Working…" : (isRegister ? "Create account" : "Sign in")}
            </button>
          </form>

          <div className="mt-6 text-sm text-[var(--slate-1)]">
            {isRegister ? (
              <>Already have an account? <Link to="/login" className="text-[var(--gold)] font-semibold underline">Sign in</Link></>
            ) : (
              <>New to Fundle? <Link to="/register" className="text-[var(--gold)] font-semibold underline">Create account</Link></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--slate-2)] mb-2">{label}</div>
      {children}
    </label>
  );
}

function RoleCard({ active, onClick, icon: Icon, title, body, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`text-left p-4 border transition ${
        active ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--bone)]" : "border-[var(--border)] bg-white hover:border-[var(--ink)]"
      }`}>
      <Icon size={18} className={active ? "text-[var(--gold-light)]" : "text-[var(--gold)]"} strokeWidth={1.6} />
      <div className="mt-3 font-serif text-lg">{title}</div>
      <div className={`mt-1 text-xs ${active ? "text-white/70" : "text-[var(--slate-1)]"}`}>{body}</div>
    </button>
  );
}
